import React, { useState, memo } from 'react';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import classNames from 'classnames';

import { Sorter } from './Sorter';
import { Filters } from './Filters';
import { LeadsReportTable } from './LeadsReportTable';
import { generateReport } from '../../actions/leadsReport';
import downloadFile from '../../utils/downloadFile';
import { DATE_FORMAT } from '../../utils/dateFormat';
import config from '../../config';
import Checkbox from '../helpers/Checkbox';
import Button from '../helpers/Button';
import { AppPage } from '../AppPage';
import { Card } from '../helpers/Card';
import { GridCard } from '../helpers/GridCard';
import { IconButton } from '../helpers/IconButton';
import { usePopupContext } from '../../contexts/PopupContext';

import style from './style.module.css';

/**
 * Leads report component.
 *
 * @param {object} props Props.
 * @param {Immutable.Map} props.user Logged user.
 * @param {Immutable.List} props.usersIds Selected user IDs list.
 * @param {string} props.usersRole Current user role.
 * @param {boolean} props.isResultsFetching Show spinner icon when set to true.
 * @param {string} props.sort Single sort model by table columns.
 * @param {object} props.sorts Multi sort model by smart filter.
 * @param {Function} props.generateReport Method for getting leads report.
 */
const LeadsReportComponent = props => {
  const { openPopup } = usePopupContext();
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const { user, userRole, usersIds, isResultsFetching, sort, sorts, generateReport } = props;
  const userName = `${user.get('firstName', '')} ${user.get('lastName', '')}`;
  const isDisabledExport = [config.RESEARCH_INTERN, config.JUNIOR_ANALYST].includes(userRole) || usersIds.size === 0;

  /** Handle generate button click. */
  const handleGenerateClick = () => {
    generateReport(sort, sorts.toJS(), hideDuplicates);
  };

  /** Handle generate button click. */
  const handleExportClick = () => {
    const url = `${config.API_BASE_URL}/api/v1/events/leads_report/export`;

    downloadFile({
      url,
      headers: {
        'content-type': 'application/json',
      },
      method: 'post',
      body: {
        user_ids: usersIds.toArray(),
        hide_duplicates: hideDuplicates,
        sort,
        sorts: sorts.toJS(),
      },
    });
  };

  /** Handle generate button click. */
  const handleHideDuplicatesCheck = event => {
    setHideDuplicates(event.target.checked);
  };

  /** Handle table hide duplicates button click. */
  const handleOpenDuplicatesPopupClick = () => {
    const subheader = (
      <p>
        If a Lead is sent to more than one client, then that Lead may exist with more than one party. Checking this box
        will cause each Target Company to only appear once. The client with the most recent activity will be the only
        one displayed.
      </p>
    );

    openPopup('InformationPopup', {
      message: {
        subheader,
        isCustomSubheader: true,
        texts: [],
      },
      header: <span>Hide Duplicates</span>,
    });
  };

  /** Handle table legend button click. */
  const handleOpenLegendPopupClick = () => {
    const subheader = (
      <ul className="pl0">
        <li key="grey">
          Rows in <span className={classNames(style.greyShadedLegend, 'fa-square fa')} /> - associated Buyer is not the
          Active buyer for the Target Company AND the Active Buyer is not Harvey
        </li>
        <li key="blue">
          Rows in <span className={classNames(style.blueShadedLegend, 'fa-square fa')} /> - associated Buyer is not the
          Active buyer for the Target Company AND the Active Buyer is Harvey
        </li>
        <li key="red">
          Tail cell in <span className={classNames(style.highlightedTailLegend, 'fa-square fa')} /> - date was
          calculated as 24 months from the &quot;Lead Sent&quot; date. BOTH &quot;Last Introduced&quot; and
          &quot;Termination&quot; are unselected OR both are selected
        </li>
        <li key="italic">
          Tail cell date in <span className="text-italic text-bold">Italic</span> - 18 month estimate used; agreement
          &quot;End Date&quot; is null
        </li>
      </ul>
    );

    openPopup('InformationPopup', {
      message: {
        subheader,
        isCustomSubheader: true,
        texts: [],
      },
      header: <span>Legend</span>,
    });
  };

  const toolbar = (
    <>
      <div className={style.hideDuplicates}>
        <Checkbox label="Hide Duplicates" name="hideDuplicates" onChange={handleHideDuplicatesCheck} />
        <IconButton
          icon="question-circle"
          onClick={handleOpenDuplicatesPopupClick}
          size="sm"
          title="About duplicates"
        />
      </div>
      <div className={style.toolbar}>
        <Button onClick={handleOpenLegendPopupClick}>
          <i aria-hidden="true" className="fa fa-info-circle" /> Legend
        </Button>
        <Button disabled={isResultsFetching} onClick={handleGenerateClick}>
          <i aria-hidden="true" className="fa fa-refresh" /> Generate
        </Button>
        <Button disabled={isDisabledExport} onClick={handleExportClick}>
          <i aria-hidden="true" className="fa fa-download" /> Export
        </Button>
      </div>
    </>
  );

  return (
    <AppPage overline={`${userName} · ${dayjs().format(DATE_FORMAT)}`} title="Leads Report" toolbar={toolbar}>
      <div className={style.layout}>
        <GridCard className={style.grid} dense>
          <LeadsReportTable />
        </GridCard>
        <Card className={style.settings}>
          <Sorter />
          <Filters />
        </Card>
      </div>
    </AppPage>
  );
};

const mapStateToProps = state => ({
  user: state.auth.get('user', null),
  userRole: state.auth.getIn(['user', 'roles', 0, 'slug']),
  usersIds: state.leadsReport.getIn(['filters', 'usersIds']),
  isResultsFetching: state.leadsReport.get('fetchingResults'),
  sort: state.leadsReport.get('sort'),
  sorts: state.leadsReport.get('sorts'),
});

const mapActionToProps = {
  generateReport,
};

const LeadsReport = connect(mapStateToProps, mapActionToProps)(memo(LeadsReportComponent));

export { LeadsReport };
